import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { applyFamilyFestEventPageTranslationAsync, getFamilyFestEventPageContentAsync, removeFamilyFestEventPageImageAsync, updateFamilyFestEventPageContentAsync, uploadFamilyFestEventPageImageAsync } from '@application/features/familyFestEventPage/familyFestEventPageThunk';
import { normalizeFamilyFestEventPageValue, validateFamilyFestEventPageValue } from '@application/helpers/family-fest-event-page-field-helper';
import { getEventLanguagesByEventIdAsync } from '@application/features/languages/languagesThunk';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { Icon } from '@iconify/react';
import { Box, Button, Skeleton, Typography, useTheme } from '@mui/material';
import AppAlert from '@ui/components/common/Alert';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { buildEventPageLayout, flagSrcFor, toFormKey } from './event-page-layout';
import EventPageLanguageButton from './EventPageLanguageButton';
import EventPageSection from './EventPageSection';
// Same viewport-bound height as DetailsTab, so only the sections scroll.
const TAB_HEIGHT = 'calc(100dvh - 146px)';
const isTextOrLinkField = (field) => field.type !== 'Image';
// Fields show what the public page shows today: the saved override, else the design default.
const toFormValues = (eventPageContent) => ({
    values: Object.fromEntries(eventPageContent.fields
        .filter(isTextOrLinkField)
        .map((field) => [
        toFormKey(field.key),
        eventPageContent.savedValues[field.key] ?? eventPageContent.defaultValues[field.key] ?? ''
    ]))
});
const isDesignDefault = (value, defaultValue) => normalizeFamilyFestEventPageValue(value) === normalizeFamilyFestEventPageValue(defaultValue);
// A value equal to the design default is sent as null, so no override is stored and the page
// keeps using the default text for that language. A cleared field is sent as '' and hides that
// element on the page.
const toRequestValues = (fields, formFieldValues, defaultValues) => Object.fromEntries(fields.filter(isTextOrLinkField).map((field) => {
    const value = normalizeFamilyFestEventPageValue(formFieldValues?.[toFormKey(field.key)]);
    return [field.key, isDesignDefault(value, defaultValues[field.key]) ? null : value];
}));
// null (no override) and '' (hidden) are different states, so compare on key presence too.
const matchesSavedValues = (requestValues, savedValues) => Object.keys(requestValues).every((fieldKey) => requestValues[fieldKey] === null ? !(fieldKey in savedValues) : requestValues[fieldKey] === savedValues[fieldKey]);
// Backend errors come back as ApiError { error } (validation, not found) — see ExceptionHandler.
const errorMessageOf = (error, fallbackMessage) => {
    const errorPayload = error;
    return errorPayload?.error || errorPayload?.message || fallbackMessage;
};
const EventPageTab = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { eventId } = useParams();
    const eventLanguagesState = useAppSelector((state) => state.languages.eventLanguages);
    const [eventPageContent, setEventPageContent] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pendingImages, setPendingImages] = useState({});
    const [isApplyDialogOpen, setIsApplyDialogOpen] = useState(false);
    const [languageIdToSwitchTo, setLanguageIdToSwitchTo] = useState(null);
    const { control, reset, setValue, getValues } = useForm({ defaultValues: { values: {} } });
    const watchedFormValues = useWatch({ control });
    const clearPendingImages = useCallback(() => {
        setPendingImages((currentPendingImages) => {
            Object.values(currentPendingImages).forEach((pendingImage) => pendingImage !== 'remove' && URL.revokeObjectURL(pendingImage.previewUrl));
            return {};
        });
    }, []);
    // Like the MeliorDev email builder: every language switch fetches that language's content.
    const loadLanguage = useCallback(async (languageId) => {
        if (!eventId)
            return;
        setIsLoading(true);
        try {
            const loadedContent = await dispatch(getFamilyFestEventPageContentAsync({ eventId, languageId })).unwrap();
            setEventPageContent(loadedContent);
            reset(toFormValues(loadedContent));
            clearPendingImages();
        }
        catch (error) {
            toast.error(errorMessageOf(error, t('events.eventDetails.eventPageTab.loadFailed')));
        }
        finally {
            setIsLoading(false);
        }
    }, [dispatch, eventId, reset, clearPendingImages, t]);
    useEffect(() => {
        loadLanguage();
    }, [loadLanguage]);
    // Every EventDetails tab loads its event's languages itself (the store's list isn't tied to an
    // event, so it may still hold another event's languages).
    useEffect(() => {
        if (eventId)
            dispatch(getEventLanguagesByEventIdAsync(eventId));
    }, [dispatch, eventId]);
    useEffect(() => () => clearPendingImages(), [clearPendingImages]);
    const fields = useMemo(() => eventPageContent?.fields ?? [], [eventPageContent]);
    const sectionLayouts = useMemo(() => buildEventPageLayout(fields), [fields]);
    const hasUnsavedText = useMemo(() => !!eventPageContent &&
        !matchesSavedValues(toRequestValues(fields, watchedFormValues.values, eventPageContent.defaultValues), eventPageContent.savedValues), [eventPageContent, fields, watchedFormValues]);
    const hasChanges = hasUnsavedText || Object.keys(pendingImages).length > 0;
    const selectedLanguageId = eventPageContent?.languageId ?? '';
    // Primary language first in the dropdown.
    const languageOptions = useMemo(() => (eventLanguagesState ?? [])
        .slice()
        .sort((first, second) => Number(second.isPrimary) - Number(first.isPrimary))
        .map((language) => ({
        languageId: language.id,
        code: language.code,
        name: language.name,
        flagSrc: flagSrcFor(language.code, language.flagUrl)
    })), [eventLanguagesState]);
    const selectedLanguageName = languageOptions.find((language) => language.languageId === selectedLanguageId)?.name ??
        (eventPageContent?.languageCode ?? '').toUpperCase();
    // Like the MeliorDev email builder: offer "apply to all" on the language that was edited last.
    const canApplyToAllLanguages = !!eventPageContent &&
        languageOptions.length > 1 &&
        !!selectedLanguageId &&
        eventPageContent.lastUpdatedLanguageId === selectedLanguageId;
    const handleLanguageSelect = (languageId) => {
        if (languageId === selectedLanguageId)
            return;
        if (hasChanges)
            setLanguageIdToSwitchTo(languageId);
        else
            loadLanguage(languageId);
    };
    const confirmLanguageSwitch = () => {
        const targetLanguageId = languageIdToSwitchTo;
        setLanguageIdToSwitchTo(null);
        if (targetLanguageId)
            loadLanguage(targetLanguageId);
    };
    const hasInvalidField = () => {
        if (!eventPageContent)
            return false;
        const formFieldValues = getValues().values;
        return fields.filter(isTextOrLinkField).some((field) => {
            const value = formFieldValues?.[toFormKey(field.key)];
            return !isDesignDefault(value, eventPageContent.defaultValues[field.key]) && !!validateFamilyFestEventPageValue(field, value);
        });
    };
    const handleSave = async () => {
        if (!eventPageContent || !eventId)
            return;
        if (hasInvalidField()) {
            toast.error(t('events.eventDetails.eventPageTab.fixErrors'));
            return;
        }
        setIsSaving(true);
        try {
            const successMessages = [];
            if (hasUnsavedText) {
                const updateResponse = await dispatch(updateFamilyFestEventPageContentAsync({
                    eventId,
                    languageId: selectedLanguageId,
                    values: toRequestValues(fields, getValues().values, eventPageContent.defaultValues)
                })).unwrap();
                if (!updateResponse.isSuccess)
                    throw updateResponse;
                if (updateResponse.message)
                    successMessages.push(updateResponse.message);
            }
            for (const [fieldKey, pendingImage] of Object.entries(pendingImages)) {
                const imageResponse = pendingImage === 'remove'
                    ? await dispatch(removeFamilyFestEventPageImageAsync({ eventId, languageId: selectedLanguageId, fieldKey })).unwrap()
                    : await dispatch(uploadFamilyFestEventPageImageAsync({ eventId, languageId: selectedLanguageId, fieldKey, file: pendingImage.file })).unwrap();
                if (!imageResponse.isSuccess)
                    throw imageResponse;
                if (imageResponse.message)
                    successMessages.push(imageResponse.message);
            }
            toast.success(successMessages.join(' ') || t('events.eventDetails.eventPageTab.saved'));
            await loadLanguage(selectedLanguageId);
        }
        catch (error) {
            toast.error(errorMessageOf(error, t('events.eventDetails.eventPageTab.saveFailed')));
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleReset = () => {
        if (eventPageContent)
            reset(toFormValues(eventPageContent));
        clearPendingImages();
    };
    const handleApplyTranslationToAll = async () => {
        if (!eventId || !selectedLanguageId)
            return;
        setIsApplyDialogOpen(false);
        setIsSaving(true);
        try {
            const applyResponse = await dispatch(applyFamilyFestEventPageTranslationAsync({ eventId, sourceLanguageId: selectedLanguageId })).unwrap();
            if (!applyResponse.isSuccess)
                throw applyResponse;
            toast.success(applyResponse.message || t('events.eventDetails.eventPageTab.appliedToAll'));
            await loadLanguage(selectedLanguageId);
        }
        catch (error) {
            toast.error(errorMessageOf(error, t('events.eventDetails.eventPageTab.applyFailed')));
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleImageChange = (fieldKey, pendingImage) => setPendingImages((currentPendingImages) => {
        const previousPendingImage = currentPendingImages[fieldKey];
        if (previousPendingImage && previousPendingImage !== 'remove')
            URL.revokeObjectURL(previousPendingImage.previewUrl);
        const nextPendingImages = { ...currentPendingImages };
        if (pendingImage)
            nextPendingImages[fieldKey] = pendingImage;
        else
            delete nextPendingImages[fieldKey];
        return nextPendingImages;
    });
    if (isLoading && !eventPageContent) {
        return (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, py: 2, height: TAB_HEIGHT, children: [_jsx(Skeleton, { variant: 'rounded', height: 48 }), _jsx(Skeleton, { variant: 'rounded', height: 420, sx: { borderRadius: 3 } }), _jsx(Skeleton, { variant: 'rounded', height: 320, sx: { borderRadius: 3 } })] }));
    }
    if (!eventPageContent) {
        return (_jsx(Box, { py: 4, children: _jsx(Typography, { variant: 'body2', color: theme.palette.text.secondary, children: t('events.eventDetails.eventPageTab.noLanguages') }) }));
    }
    return (_jsxs(Box, { display: 'flex', flexDirection: 'column', height: TAB_HEIGHT, minHeight: 0, pt: 2, children: [_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.5, flexShrink: 0, children: [_jsx(Typography, { variant: 'h4', flex: 1, minWidth: 0, children: t('events.eventDetails.eventPageTab.title') }), _jsx(EventPageLanguageButton, { languages: languageOptions, selectedLanguageId: selectedLanguageId, onSelect: handleLanguageSelect })] }), canApplyToAllLanguages && (_jsx(AppAlert, { severity: 'info', icon: false, action: _jsx(Button, { variant: 'contained', size: 'small', color: 'inherit', disabled: isSaving || isLoading, endIcon: _jsx(Icon, { icon: 'solar:refresh-bold', width: 18 }), sx: { whiteSpace: 'nowrap' }, onClick: () => hasUnsavedText
                        ? toast.info(t('events.eventDetails.eventPageTab.saveBeforeApply'))
                        : setIsApplyDialogOpen(true), children: t('events.eventDetails.eventPageTab.applyToAllButton') }), sx: {
                    width: '100%',
                    flexShrink: 0,
                    mb: 1.5,
                    rowGap: 1,
                    '& .MuiAlert-message': { minWidth: 200 },
                    '& .MuiAlert-action': { flexShrink: 0, alignItems: 'center' }
                }, children: t('events.eventDetails.eventPageTab.applyToAllAlert', { language: selectedLanguageName }) })), _jsx(Box, { flex: 1, minHeight: 0, sx: { overflowY: 'auto', opacity: isLoading ? 0.5 : 1, pointerEvents: isLoading ? 'none' : 'auto' }, children: _jsx(Box, { display: 'flex', flexDirection: 'column', gap: 2, pb: 1, children: sectionLayouts.map((sectionLayout) => (_jsx(EventPageSection
                    // Remount per language: the inputs keep their DOM value when only their form path changes.
                    , { layout: sectionLayout, control: control, setValue: setValue, defaultValues: eventPageContent.defaultValues, savedValues: eventPageContent.savedValues, pendingImages: pendingImages, onImageChange: handleImageChange }, `${sectionLayout.section}-${selectedLanguageId}`))) }) }), _jsxs(Box, { display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, pt: 1.5, pb: 2, flexShrink: 0, children: [_jsx(Box, { flex: 1, display: { xs: 'none', sm: 'block' } }), hasChanges && (_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', disabled: isSaving, onClick: handleReset, children: t('actions.reset') })), _jsx(Button, { variant: 'contained', color: 'primary', size: 'large', disabled: isSaving || isLoading || !hasChanges, onClick: handleSave, children: t('actions.save') })] }), _jsx(ConfirmationDialog, { open: isApplyDialogOpen, onClose: () => setIsApplyDialogOpen(false), title: t('events.eventDetails.eventPageTab.applyDialogTitle'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', size: 'large', onClick: () => setIsApplyDialogOpen(false), children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'primary', size: 'large', onClick: handleApplyTranslationToAll, children: t('events.eventDetails.eventPageTab.applyDialogConfirm') })] }), children: _jsx(Box, { px: 2.5, py: 2, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.eventPageTab.applyDialogBody', { language: selectedLanguageName }) }) }) }), _jsx(ConfirmationDialog, { open: languageIdToSwitchTo !== null, onClose: () => setLanguageIdToSwitchTo(null), title: t('events.eventDetails.eventPageTab.unsavedDialogTitle'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', size: 'large', onClick: () => setLanguageIdToSwitchTo(null), children: t('events.eventDetails.eventPageTab.keepEditing') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'primary', size: 'large', onClick: confirmLanguageSwitch, children: t('events.eventDetails.eventPageTab.discardAndSwitch') })] }), children: _jsx(Box, { px: 2.5, py: 2, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.eventPageTab.unsavedDialogBody', { language: selectedLanguageName }) }) }) })] }));
};
export default EventPageTab;
